import styled from 'styled-components';

import StyledButton from 'components/Buttons/Button';
import BaseToggleCard from 'components/ToggleCard';
import { ColorPalette } from 'theme/colors';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const HelperText = styled.p`
  font-size: 14px;
  line-height: 1.4;
  color: ${ColorPalette.HEX.DimGray};
  margin: 0;
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: ${ColorPalette.HEX.White};
  border: 1px solid ${ColorPalette.HEX.BrightGray};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.primaryBackgroundColor};
  }
`;

export const AddButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AddButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  img {
    width: 26px;
    height: 26px;
  }
`;

export const AddButtonLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryBackgroundColor};
`;

export const Chevron = styled.img`
  width: 10px;
  height: 16px;
`;

export const ExceptionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ExceptionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${ColorPalette.HEX.White};
  border: 1px solid ${ColorPalette.HEX.BrightGray};
  border-radius: 12px;
`;

export const ExceptionSummary = styled.span`
  font-size: 14px;
  color: ${ColorPalette.HEX.DimGray};
  flex: 1;
`;

export const ExceptionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;

  img {
    width: 18px;
    height: 18px;
  }
`;

export const DrawerContainer = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background-color: ${ColorPalette.HEX.White};
`;

export const DrawerBody = styled.section`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

export const InfoCard = styled.div`
  border: 1px solid ${ColorPalette.HEX.BrightGray};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const InfoCardTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: ${ColorPalette.HEX.DimGray};
`;

export const InfoCardText = styled.p`
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  color: ${ColorPalette.HEX.DimGray};
`;

export const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${ColorPalette.HEX.DimGray};
  margin-bottom: 6px;
  display: block;
`;

export const SelectField = styled.button<{ $disabled?: boolean; $hasError?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: ${({ $disabled }) => ($disabled ? ColorPalette.HEX.Lotion : ColorPalette.HEX.White)};
  border: 1px solid ${({ $hasError }) => ($hasError ? '#e53935' : ColorPalette.HEX.BrightGray)};
  border-radius: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  color: ${ColorPalette.HEX.DimGray};
  text-align: left;
`;

export const SelectFieldText = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  color: ${({ $placeholder }) => ($placeholder ? ColorPalette.HEX.Gainsboro : ColorPalette.HEX.DimGray)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SelectFieldChevron = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 8px;
`;

export const NativeSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  background: ${ColorPalette.HEX.White};
  border: 1px solid ${({ $hasError }) => ($hasError ? '#e53935' : ColorPalette.HEX.BrightGray)};
  border-radius: 12px;
  font-size: 14px;
  color: ${ColorPalette.HEX.DimGray};
  appearance: none;
  cursor: pointer;
  background-image: url('/corporate/images/chevron.svg');
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 14px;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ToggleCard = styled(BaseToggleCard)`
  border-radius: 12px;
`;

export const FormFooter = styled.div`
  padding: 16px 24px 24px;
  position: sticky;
  bottom: 0;
  background: ${ColorPalette.HEX.White};
`;

export const SubmitButton = styled(StyledButton)`
  font-size: 16px;
  font-weight: 600;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid ${ColorPalette.HEX.BrightGray};
  border-radius: 24px;
  font-size: 14px;
  background-color: ${ColorPalette.HEX.White};
  background-image: url('/corporate/images/search.svg');
  background-repeat: no-repeat;
  background-position: 14px center;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primaryBackgroundColor};
  }
`;

export const OptionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const OptionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid ${ColorPalette.HEX.BrightGray};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

export const OptionItemLabel = styled.span`
  font-size: 14px;
  color: ${ColorPalette.HEX.DimGray};
  flex: 1;
`;

export const SelectedDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primaryBackgroundColor};
`;

export const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 14px;
  color: ${ColorPalette.HEX.Gainsboro};
`;
